import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Loader2, Sparkles, CheckCircle2, Clock, ShieldCheck, Rocket,
  Search, MapPin, Megaphone, FileText, LineChart, ArrowRight,
} from "lucide-react";
import { trackEvent } from "../lib/analytics";

/**
 * V230 F0 — SEO Command Center waitlist landing (/seo-command)
 *
 * Pre-launch collector for the Organic + Local SEO paid module.
 * Pricing: €49 / €149 / €399 subscription tiers (first 50 get 30% off).
 * Flow: pick tier → leave email → confirmation → waitlist.
 */
export default function SeoCommandWaitlistPage() {
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("growth");
  const [domain, setDomain] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverMessage, setServerMessage] = useState("");
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    trackEvent("view_seo_command_waitlist", {
      source: "seo_command_landing",
    });
    axios
      .get(`${API}/seo-command/pricing`)
      .then((r) => setPricing(r.data))
      .catch(() => {});
  }, []);

  const canSubmit = useMemo(
    () => email.trim().includes("@") && email.includes("."),
    [email]
  );

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/seo-command/waitlist`, {
        email: email.trim(),
        plan,
        domain: domain.trim() || null,
        language: "sk",
        utm_source: new URLSearchParams(window.location.search).get("utm_source"),
        utm_campaign: new URLSearchParams(window.location.search).get("utm_campaign"),
      });
      setSuccess(true);
      setServerMessage(r.data?.message || "Ďakujeme! Si na waitliste.");
      trackEvent("seo_command_waitlist_joined", { plan, source: "seo_command_landing" });
    } catch (err) {
      setServerMessage(err?.response?.data?.detail || "Nepodarilo sa odoslať. Skús znova.");
    } finally {
      setSubmitting(false);
    }
  };

  const tiers = pricing?.tiers || null;
  const seatsLeft = pricing?.early_bird?.seats_left ?? 47;
  const discountPct = pricing?.early_bird?.discount_pct ?? 30;

  return (
    <div className="min-h-screen bg-[#0A0F2E] text-slate-100">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-12" data-testid="seo-command-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-xs font-semibold tracking-wide uppercase mb-6">
          <Rocket className="w-3.5 h-3.5" />
          Pre-launch · Prvých {seatsLeft} miest · {discountPct}% zľava
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
          SEO Command Center.<br />
          <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-amber-400 bg-clip-text text-transparent">
            Organický + Local SEO v jednom paneli.
          </span>
        </h1>
        <p className="text-lg sm:text-xl text-slate-300 max-w-3xl leading-relaxed">
          Google Search Console sync, Google Business Profile automatizácia, AI content briefs,
          rank tracking a hromadná generácia článkov — všetko riadené z jedného miesta.
          Spúšťame v Q1 2026. Zapíš sa teraz a odomkneš <span className="text-amber-300 font-semibold">{discountPct}% zľavu</span> na prvé 3 mesiace.
        </p>
      </section>

      {/* Features preview */}
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="seo-command-features">
          {FEATURE_CARDS.map((f) => (
            <div
              key={f.title}
              className="bg-[#111634] border border-slate-700/60 rounded-xl p-5 hover:border-violet-500/50 transition"
            >
              <f.icon className="w-6 h-6 text-violet-400 mb-3" />
              <h3 className="text-base font-semibold mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
          Tri jednoduché balíčky
        </h2>
        <div className="grid sm:grid-cols-3 gap-4" data-testid="seo-command-tiers">
          {["starter", "growth", "agency"].map((key) => {
            const t = tiers?.[key] || DEFAULT_TIERS[key];
            const isActive = plan === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`tier-${key}`}
                onClick={() => setPlan(key)}
                className={`text-left bg-[#111634] rounded-xl p-5 border transition ${
                  isActive
                    ? "border-violet-400 ring-2 ring-violet-500/40"
                    : "border-slate-700/60 hover:border-slate-500"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold">{t.name || `Tier ${key}`}</h3>
                  {isActive && (
                    <CheckCircle2 className="w-4 h-4 text-violet-400" />
                  )}
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-bold tracking-tight">€{t.price_eur}</span>
                  <span className="text-sm text-slate-400">/mes</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">{t.ideal_for}</p>
                <ul className="space-y-1.5">
                  {(t.features || []).slice(0, 4).map((feat, i) => (
                    <li key={i} className="text-xs text-slate-300 flex gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </section>

      {/* Waitlist form */}
      <section className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-[#111634] border border-violet-500/30 rounded-2xl p-6 sm:p-8" data-testid="seo-command-waitlist-form">
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="text-2xl font-bold mb-1">Zapíš sa na waitlist</h2>
              <p className="text-sm text-slate-400 mb-4">
                Keď spustíme SEO Command (Q1 2026), dostaneš email s {discountPct}% zľavovým kódom.
                Žiadne spam. Môžeš sa kedykoľvek odhlásiť.
              </p>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                  Email <span className="text-rose-400">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="meno@firma.sk"
                  data-testid="waitlist-email"
                  className="w-full bg-[#0A0F2E] border border-slate-700 rounded-lg px-3.5 py-2.5 text-sm focus:border-violet-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                  Doména (voliteľné)
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="firma.sk"
                  data-testid="waitlist-domain"
                  className="w-full bg-[#0A0F2E] border border-slate-700 rounded-lg px-3.5 py-2.5 text-sm focus:border-violet-400 focus:outline-none"
                />
              </div>

              <div className="pt-1">
                <span className="text-xs text-slate-400">
                  Záujem o: <span className="text-violet-300 font-semibold uppercase">{plan}</span>
                </span>
              </div>

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                data-testid="waitlist-submit"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-3 transition"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Chcem {discountPct}% zľavu
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="flex items-center gap-3 text-xs text-slate-500 pt-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                GDPR · EU-native · dáta ostávajú v EU
              </div>

              {serverMessage && !success && (
                <p className="text-sm text-rose-400 mt-2" data-testid="waitlist-error">{serverMessage}</p>
              )}
            </form>
          ) : (
            <div className="text-center py-6" data-testid="waitlist-success">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Si na waitliste ✨</h2>
              <p className="text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
                {serverMessage}
              </p>
              <p className="mt-6 text-xs text-slate-500 flex items-center justify-center gap-2">
                <Clock className="w-3.5 h-3.5" />
                Očakávaný launch: Q1 2026
              </p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ-ish strip */}
      <section className="max-w-4xl mx-auto px-6 pb-20 pt-4">
        <div className="grid sm:grid-cols-3 gap-6 text-sm">
          <div>
            <h3 className="font-semibold mb-1">Kedy to spustíte?</h3>
            <p className="text-slate-400">Q1 2026. Prví klienti začnú testovať v Marci.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">Prečo waitlist, nie priamo platba?</h3>
            <p className="text-slate-400">Chceme nabehnúť s produktom ktorý presne rieši tvoje bolesti — pýtame sa.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">Dostanem naozaj {discountPct}% zľavu?</h3>
            <p className="text-slate-400">Áno, platí pre prvých 50 klientov a trvá prvé 3 mesiace.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

const FEATURE_CARDS = [
  { icon: Search, title: "GSC OAuth sync",
    desc: "Denný import impresions, klikov a pozícií priamo z Google Search Console." },
  { icon: MapPin, title: "Google Business Profile",
    desc: "Automatické odpovede na recenzie, lokálny rank tracking v 10+ mestách." },
  { icon: FileText, title: "AI Content Briefs",
    desc: "Výstup pre copywritera: SERP výskum, otázky, LSI slová, vnútorné odkazy." },
  { icon: LineChart, title: "Rank tracking",
    desc: "Denné sledovanie 100-2 500 keywordov cez SK/CZ/EN pre každú doménu." },
  { icon: Megaphone, title: "Review Automator",
    desc: "AI navrhne odpovede na GBP recenzie — ty len schváliš a odošleš." },
  { icon: FileText, title: "Bulk Gen + WP plugin",
    desc: "Hromadná generácia článkov → priame publikovanie do WordPressu." },
];

const DEFAULT_TIERS = {
  starter: {
    name: "Starter", price_eur: 49, ideal_for: "Freelancers · 1 projekt",
    features: ["GSC OAuth sync", "100 keywordov", "10 content briefs/mes", "1 GBP lokalita"],
  },
  growth: {
    name: "Growth", price_eur: 149, ideal_for: "SMBs · 5 projektov",
    features: ["Všetko zo Starter", "500 keywordov", "50 content briefs/mes", "5 GBP lokalít"],
  },
  agency: {
    name: "Agency", price_eur: 399, ideal_for: "Agentúry · white-label",
    features: ["Všetko z Growth", "2 500 keywordov", "300 briefs/mes", "20 GBP lokalít"],
  },
};
